import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ForumRoom } from '../types';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconEye,
  IconEyeOff,
  IconMessages,
} from '@tabler/icons-react';
import {
  listRooms,
  createRoom,
  updateRoom,
  deleteRoom,
  toggleRoomActive,
} from '../services/forumService';
import RoomModal from '../components/RoomModal';

export default function ForumRoomsPage() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<ForumRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<ForumRoom | null>(null);

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const result = await listRooms({ includeInactive: true });
      setRooms(result.rooms);
    } catch (error) {
      console.error('Error loading rooms:', error);
      alert('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRoom(null);
    setShowModal(true);
  };

  const handleEdit = (room: ForumRoom) => {
    setEditingRoom(room);
    setShowModal(true);
  };

  const handleDelete = async (room: ForumRoom) => {
    if (!confirm(`Are you sure you want to delete "${room.name}"?`)) {
      return;
    }

    try {
      await deleteRoom(room.id);
      await loadRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
      alert('Failed to delete room');
    }
  };

  const handleToggleActive = async (room: ForumRoom) => {
    try {
      await toggleRoomActive(room.id, !room.isActive);
      await loadRooms();
    } catch (error) {
      console.error('Error toggling room:', error);
      alert('Failed to toggle room status');
    }
  };

  const handleSave = async (
    roomData: Omit<ForumRoom, 'id' | 'topicCount' | 'postCount' | 'createdAt'>
  ) => {
    try {
      if (editingRoom) {
        await updateRoom(editingRoom.id, roomData);
      } else {
        await createRoom(roomData);
      }
      setShowModal(false);
      await loadRooms();
    } catch (error) {
      console.error('Error saving room:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Forum</div>
          <h2 className="page-title">Rooms</h2>
        </div>
        <div className="col-auto">
          <button onClick={handleAdd} className="btn btn-primary">
            <IconPlus className="icon" />
            Add Room
          </button>
        </div>
      </div>

      {/* Rooms Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Name</th>
                <th>Description</th>
                <th>Topics</th>
                <th>Posts</th>
                <th>Status</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No rooms found
                  </td>
                </tr>
              ) : (
                rooms.map(room => (
                  <tr key={room.id}>
                    <td>{room.order}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        {room.imageUrl && (
                          <img
                            src={room.imageUrl}
                            alt={room.name}
                            className="avatar avatar-sm me-2"
                          />
                        )}
                        <span className="text-reset">{room.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="text-muted">{room.description}</div>
                    </td>
                    <td>{room.topicCount}</td>
                    <td>{room.postCount}</td>
                    <td>
                      <span
                        className={`badge text-white ${room.isActive ? 'bg-success' : 'bg-secondary'}`}
                      >
                        {room.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          onClick={() => navigate(`/forum-rooms/${room.id}/topics`)}
                          className="btn btn-icon"
                          title="View Topics"
                        >
                          <IconMessages className="icon" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(room)}
                          className="btn btn-icon"
                          title={room.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {room.isActive ? (
                            <IconEyeOff className="icon" />
                          ) : (
                            <IconEye className="icon" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(room)}
                          className="btn btn-icon"
                          title="Edit"
                        >
                          <IconPencil className="icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(room)}
                          className="btn btn-icon text-danger"
                          title="Delete"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <RoomModal room={editingRoom} onSave={handleSave} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}
